import { useState, useEffect, useMemo } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle } from 'lucide-react';

/**
 * Game Date/Time Picker
 * - Date input for selecting the date
 * - Hour/Minute dropdowns for time
 * - Minutes restricted to 15-minute increments (00, 15, 30, 45)
 * - Unusual time warning for games before 4 AM or after 11 PM
 */

// Generate hour options (12-hour format with AM/PM)
const HOURS = Array.from({ length: 24 }, (_, i) => {
  const hour24 = i;
  const hour12 = i === 0 ? 12 : i > 12 ? i - 12 : i;
  const period = i < 12 ? 'AM' : 'PM';
  return {
    value: hour24.toString().padStart(2, '0'),
    label: `${hour12}:00 ${period}`,
    displayHour: hour12,
    period,
  };
});

// Minutes in 15-minute increments only
const MINUTES = ['00', '15', '30', '45'];

// Check if time is unusual (before 4 AM or after 11 PM)
const isUnusualTime = (hour) => {
  const h = parseInt(hour);
  return h < 4 || h >= 23;
};

const GameTimePicker = ({
  value, // datetime-local format: "2024-01-15T14:30"
  onChange,
  required = false,
  className = '',
  label = 'Date & Time',
  minDate = null, // Optional min date
  maxDate = null, // Optional max date
  helpText = '',
}) => {
  // Parse the datetime-local value
  const parseValue = (val) => {
    if (!val) return { date: '', hour: '', minute: '00' };
    const [datePart, timePart] = val.split('T');
    if (!timePart) return { date: datePart || '', hour: '', minute: '00' };
    const [hour, minute] = timePart.split(':');
    // Round minute to nearest 15
    const roundedMinute = MINUTES.reduce((prev, curr) => 
      Math.abs(parseInt(curr) - parseInt(minute)) < Math.abs(parseInt(prev) - parseInt(minute)) ? curr : prev
    );
    // Ensure hour is zero-padded
    const paddedHour = hour ? hour.padStart(2, '0') : '';
    return {
      date: datePart,
      hour: paddedHour,
      minute: roundedMinute || '00',
    };
  };

  // Initialize state from initial value prop
  const initialParsed = useMemo(() => parseValue(value), []);
  const [selectedDate, setSelectedDate] = useState(initialParsed.date);
  const [selectedHour, setSelectedHour] = useState(initialParsed.hour);
  const [selectedMinute, setSelectedMinute] = useState(initialParsed.minute);
  const [showWarning, setShowWarning] = useState(initialParsed.hour ? isUnusualTime(initialParsed.hour) : false);

  // Update state when value prop changes (for edit form)
  useEffect(() => {
    const parsed = parseValue(value);
    setSelectedDate(parsed.date);
    setSelectedHour(parsed.hour);
    setSelectedMinute(parsed.minute);
    setShowWarning(parsed.hour ? isUnusualTime(parsed.hour) : false);
  }, [value]);

  // Update parent when values change
  const handleChange = (type, val) => {
    let newDate = selectedDate;
    let newHour = selectedHour;
    let newMinute = selectedMinute;

    if (type === 'date') {
      newDate = val;
      setSelectedDate(val);
    } else if (type === 'hour') {
      newHour = val;
      setSelectedHour(val);
      setShowWarning(isUnusualTime(val));
    } else if (type === 'minute') {
      newMinute = val;
      setSelectedMinute(val);
    }

    // Build datetime-local string
    if (newDate && newHour) {
      const datetime = `${newDate}T${newHour}:${newMinute}`;
      onChange(datetime);
    } else if (newDate && !newHour) {
      // Just date, no time yet - don't update
    }
  };

  // Format display for hour dropdown
  const getHourDisplay = (hour) => {
    if (!hour) return '';
    const h = parseInt(hour);
    const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const period = h < 12 ? 'AM' : 'PM';
    return `${hour12} ${period}`;
  };

  // Calculate min date (today by default)
  const computedMinDate = useMemo(() => {
    if (minDate) return minDate;
    return new Date().toISOString().split('T')[0];
  }, [minDate]);

  return (
    <div className={className}>
      {label && (
        <Label className="text-[#0B0B0B] font-medium">
          {label} {required && '*'}
        </Label>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-1.5">
        {/* Date Input */}
        <Input
          type="date"
          value={selectedDate}
          onChange={(e) => handleChange('date', e.target.value)}
          min={computedMinDate}
          max={maxDate}
          className="h-12 rounded-[12px] border-[#E5E7EB] focus:border-[#FF6A00] focus:ring-[#FF6A00]/20"
          data-testid="game-date-input"
          required={required}
        />

        {/* Hour Dropdown */}
        <Select
          value={selectedHour}
          onValueChange={(val) => handleChange('hour', val)}
        >
          <SelectTrigger 
            className="h-12 rounded-[12px] border-[#E5E7EB] focus:border-[#FF6A00] focus:ring-[#FF6A00]/20"
            data-testid="game-hour-select"
          >
            <SelectValue placeholder="Hour" />
          </SelectTrigger>
          <SelectContent>
            {HOURS.map(h => (
              <SelectItem key={h.value} value={h.value}>
                {h.displayHour} {h.period}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Minute Dropdown (15-min increments) */}
        <Select
          value={selectedMinute}
          onValueChange={(val) => handleChange('minute', val)}
        >
          <SelectTrigger 
            className="h-12 rounded-[12px] border-[#E5E7EB] focus:border-[#FF6A00] focus:ring-[#FF6A00]/20"
            data-testid="game-minute-select"
          >
            <SelectValue placeholder="Min">
              :{selectedMinute}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {MINUTES.map(m => (
              <SelectItem key={m} value={m}>:{m}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Help text */}
      {helpText && !showWarning && (
        <p className="text-xs text-zinc-500 mt-1">{helpText}</p>
      )}

      {/* Unusual time warning */}
      {showWarning && (
        <div className="flex items-center gap-2 mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <p className="text-xs text-amber-700">
            This game time is unusual and may reduce turnout.
          </p>
        </div>
      )}
    </div>
  );
};

export default GameTimePicker;
